import { Category, Owner, Location, Item } from '../types';

const owners: Record<string, Owner> = {
  johnSmith: {
    id: 'john-smith',
    name: 'John Smith',
    rating: 4.5,
    totalRatings: 24
  },
  sarahJohnson: {
    id: 'sarah-johnson',
    name: 'Sarah Johnson',
    rating: 5,
    totalRatings: 31
  },
  mikeWilson: {
    id: 'mike-wilson',
    name: 'Mike Wilson',
    rating: 4,
    totalRatings: 15
  },
  emmaDavis: {
    id: 'emma-davis',
    name: 'Emma Davis',
    rating: 4.8,
    totalRatings: 42
  },
  lisaBrown: {
    id: 'lisa-brown',
    name: 'Lisa Brown',
    rating: 4.2,
    totalRatings: 18
  },
  davidClark: {
    id: 'david-clark',
    name: 'David Clark',
    rating: 4.7,
    totalRatings: 28
  },
  amyWhite: {
    id: 'amy-white',
    name: 'Amy White',
    rating: 4.9,
    totalRatings: 35
  }
};

// Helper function to create placeholder images
const createPlaceholderImages = (itemName: string, count: number = 3): string[] => {
  return Array.from({ length: count }, (_, i) => 
    `https://picsum.photos/seed/${itemName.toLowerCase().replace(/\s+/g, '-')}-${i + 1}/600/400`
  );
};

// Sample locations
const locations: Record<string, Location> = {
  cambridge: {
    address: "15 Mill Road",
    city: "Cambridge",
    postcode: "CB1 2AB",
    coordinates: {
      latitude: 52.2053,
      longitude: 0.1218
    }
  },
  newmarket: {
    address: "42 High Street",
    city: "Newmarket",
    postcode: "CB8 8JP",
    coordinates: {
      latitude: 52.2429,
      longitude: 0.4072
    }
  },
  ely: {
    address: "8 Market Street",
    city: "Ely",
    postcode: "CB7 4PB",
    coordinates: {
      latitude: 52.3994,
      longitude: 0.2621
    }
  },
  haverhill: {
    address: "23 Queen Street",
    city: "Haverhill",
    postcode: "CB9 8DT",
    coordinates: {
      latitude: 52.0831,
      longitude: 0.4376
    }
  },
  royston: {
    address: "56 London Road",
    city: "Royston",
    postcode: "SG8 9JL",
    coordinates: {
      latitude: 52.0474,
      longitude: -0.0205
    }
  }
};

// Helper function to create an item with required location
const createItem = (
  id: string,
  name: string,
  description: string,
  dailyRateGBP: number,
  owner: Owner,
  minDays: number,
  maxDays: number,
  category: string,
  location: Location,
  rentalPeriods: { startDate: Date; endDate: Date; returned: boolean; renterId?: string; renterName?: string; }[] = []
): Item => ({
  id,
  name,
  description,
  dailyRateGBP: dailyRateGBP,
  images: createPlaceholderImages(name),
  ownerId: owner.id,
  minDays,
  maxDays,
  category,
  location,
  availability: true,
  rentalPeriods: rentalPeriods.map(period => ({
    startDate: period.startDate.toISOString(),
    endDate: period.endDate.toISOString(),
    returned: period.returned,
    renterId: period.renterId,
    renterName: period.renterName
  }))
});

export const categories: Category[] = [
  {
    id: 'garden-tools',
    name: 'Garden Tools',
    description: 'Tools for gardening and landscaping',
    items: [
      createItem(
        'lawn-mower-1',
        'Professional Lawn Mower',
        'High-powered lawn mower suitable for large gardens',
        25.00,
        owners.johnSmith,
        1,
        7,
        'garden-tools',
        locations.cambridge,
        [{ startDate: new Date('2024-04-01'), endDate: new Date('2024-04-03'), returned: true }]
      ),
      createItem(
        'hedge-trimmer-1',
        'Cordless Hedge Trimmer',
        '20V battery-powered hedge trimmer with extended reach. Includes two batteries and charger.',
        15,
        owners.sarahJohnson,
        1,
        5,
        'garden-tools',
        locations.newmarket
      ),
      createItem(
        'leaf-blower-1',
        'Backpack Leaf Blower',
        'Professional-grade gas-powered leaf blower. Perfect for large yards and heavy-duty cleanup.',
        20,
        owners.mikeWilson,
        1,
        3,
        'garden-tools',
        locations.ely,
        [{ startDate: new Date('2024-03-25'), endDate: new Date('2024-03-28'), returned: true }]
      ),
      createItem(
        'tiller-1',
        'Garden Tiller',
        'Front-tine tiller ideal for garden preparation and soil cultivation. Easy to maneuver and reliable.',
        30,
        owners.emmaDavis,
        1,
        4,
        'garden-tools',
        locations.haverhill,
        [{ startDate: new Date('2024-03-20'), endDate: new Date('2024-04-20'), returned: true }]
      )
    ]
  },
  {
    id: 'power-tools',
    name: 'Power Tools',
    description: 'Professional-grade power tools for any project',
    items: [
      createItem(
        'drill-1',
        'Cordless Drill',
        '18V drill with variable speed and hammer function. Includes carry case, charger, and various drill bits.',
        12,
        owners.mikeWilson,
        1,
        7,
        'power-tools',
        locations.royston
      ),
      createItem(
        'circular-saw-1',
        'Circular Saw',
        'Professional grade circular saw with laser guide. Perfect for precise cuts in wood and other materials.',
        20,
        owners.emmaDavis,
        1,
        5,
        'power-tools',
        locations.cambridge,
        [{ startDate: new Date('2024-03-15'), endDate: new Date('2024-03-30'), returned: true }]
      ),
      createItem(
        'table-saw-1',
        'Portable Table Saw',
        '10-inch portable table saw with stand. Great for job sites or home workshops.',
        35,
        owners.davidClark,
        2,
        7,
        'power-tools',
        locations.newmarket
      ),
      createItem(
        'air-compressor-1',
        'Air Compressor',
        '6-gallon pancake air compressor. Includes basic air tool kit.',
        25,
        owners.johnSmith,
        1,
        5,
        'power-tools',
        locations.ely
      )
    ]
  },
  {
    id: 'kitchen-appliances',
    name: 'Kitchen Appliances',
    description: 'High-end kitchen appliances for special occasions',
    items: [
      createItem(
        'stand-mixer-1',
        'Professional Stand Mixer',
        '5-quart stand mixer with multiple attachments. Perfect for baking and cooking.',
        15,
        owners.lisaBrown,
        2,
        7,
        'kitchen-appliances',
        locations.cambridge
      ),
      createItem(
        'food-processor-1',
        'Food Processor',
        '12-cup food processor with multiple blades and attachments.',
        12,
        owners.amyWhite,
        1,
        5,
        'kitchen-appliances',
        locations.newmarket
      ),
      createItem(
        'sous-vide-1',
        'Sous Vide Precision Cooker',
        'Professional-grade sous vide cooker with WiFi connectivity.',
        18,
        owners.davidClark,
        2,
        7,
        'kitchen-appliances',
        locations.ely,
        [{ startDate: new Date('2024-03-20'), endDate: new Date('2024-03-25'), returned: true }]
      ),
      createItem(
        'dehydrator-1',
        'Food Dehydrator',
        '6-tray food dehydrator with digital controls.',
        10,
        owners.emmaDavis,
        2,
        10,
        'kitchen-appliances',
        locations.haverhill
      )
    ]
  },
  {
    id: 'camping-gear',
    name: 'Camping Gear',
    description: 'Quality camping equipment for outdoor adventures',
    items: [
      createItem(
        'tent-1',
        '4-Person Tent',
        'Spacious 4-person tent with rainfly and vestibule. Easy setup and great ventilation.',
        20,
        owners.davidClark,
        2,
        14,
        'camping-gear',
        locations.cambridge
      ),
      createItem(
        'sleeping-bags-1',
        'Sleeping Bag Set',
        'Set of 2 three-season sleeping bags rated for 20°F. Includes compression sacks.',
        15,
        owners.amyWhite,
        2,
        14,
        'camping-gear',
        locations.newmarket
      ),
      createItem(
        'camp-stove-1',
        'Camp Stove',
        '2-burner propane camp stove. Perfect for outdoor cooking.',
        12,
        owners.mikeWilson,
        2,
        10,
        'camping-gear',
        locations.ely,
        [{ startDate: new Date('2025-06-25'), endDate: new Date('2025-07-10'), returned: false }]
      )
    ]
  },
  {
    id: 'party-supplies',
    name: 'Party Supplies',
    description: 'Everything you need for a successful event',
    items: [
      createItem(
        'karaoke-1',
        'Karaoke System',
        'Complete karaoke system with two wireless microphones and LED party lights.',
        30,
        owners.lisaBrown,
        1,
        3,
        'party-supplies',
        locations.cambridge
      ),
      createItem(
        'popcorn-machine-1',
        'Popcorn Machine',
        'Commercial-style popcorn maker with cart. Great for parties and events.',
        25,
        owners.emmaDavis,
        1,
        3,
        'party-supplies',
        locations.haverhill
      ),
      createItem(
        'tables-chairs-1',
        'Tables and Chairs Set',
        'Set of 2 folding tables and 16 chairs. Perfect for outdoor gatherings.',
        40,
        owners.sarahJohnson,
        1,
        5,
        'party-supplies',
        locations.royston
      )
    ]
  },
  {
    id: 'sports-equipment',
    name: 'Sports Equipment',
    description: 'Quality sports gear for all activities',
    items: [
      createItem(
        'bike-1',
        'Mountain Bike',
        'High-quality mountain bike with front suspension. Includes helmet and basic repair kit.',
        25,
        owners.davidClark,
        1,
        7,
        'sports-equipment',
        locations.newmarket
      ),
      createItem(
        'kayak-1',
        'Recreational Kayak',
        '10-foot recreational kayak with paddle and life vest. Perfect for calm waters.',
        35,
        owners.amyWhite,
        1,
        5,
        'sports-equipment',
        locations.ely
      ),
      createItem(
        'golf-clubs-1',
        'Golf Club Set',
        'Complete set of golf clubs with bag. Includes driver, irons, wedges, and putter.',
        30,
        owners.johnSmith,
        1,
        7,
        'sports-equipment',
        locations.cambridge
      )
    ]
  },
  {
    id: 'diy-tools',
    name: 'DIY & Hand Tools',
    description: 'Essential hand tools for home improvement projects',
    items: [
      createItem(
        'tool-set-1',
        'Complete Tool Set',
        '150-piece tool set including wrenches, screwdrivers, pliers, and measuring tools.',
        18.00,
        owners.johnSmith,
        1,
        7,
        'diy-tools',
        locations.cambridge
      ),
      createItem(
        'ladder-1',
        'Extension Ladder',
        '20-foot aluminum extension ladder with stabilizer bar. Perfect for painting and maintenance.',
        22.00,
        owners.mikeWilson,
        1,
        5,
        'diy-tools',
        locations.newmarket
      ),
      createItem(
        'paint-sprayer-1',
        'Paint Sprayer',
        'Professional airless paint sprayer. Great for large painting projects.',
        28.00,
        owners.davidClark,
        1,
        4,
        'diy-tools',
        locations.ely
      )
    ]
  },
  {
    id: 'cleaning-equipment',
    name: 'Cleaning Equipment',
    description: 'Professional cleaning tools and machines',
    items: [
      createItem(
        'pressure-washer-1',
        'High-Pressure Washer',
        '3000 PSI electric pressure washer with multiple nozzles. Perfect for outdoor cleaning.',
        25.00,
        owners.sarahJohnson,
        1,
        3,
        'cleaning-equipment',
        locations.cambridge
      ),
      createItem(
        'steam-cleaner-1',
        'Steam Cleaner',
        'Multi-purpose steam cleaner for floors, upholstery, and more.',
        20.00,
        owners.lisaBrown,
        1,
        4,
        'cleaning-equipment',
        locations.haverhill
      ),
      createItem(
        'wet-vacuum-1',
        'Wet/Dry Vacuum',
        'Industrial wet/dry vacuum with HEPA filter. Great for construction cleanup.',
        15.00,
        owners.emmaDavis,
        1,
        5,
        'cleaning-equipment',
        locations.royston
      )
    ]
  },
  {
    id: 'moving-equipment',
    name: 'Moving Equipment',
    description: 'Tools and equipment for moving and storage',
    items: [
      createItem(
        'dolly-1',
        'Appliance Dolly',
        'Heavy-duty appliance dolly with straps. Rated for up to 800 lbs.',
        15.00,
        owners.mikeWilson,
        1,
        3,
        'moving-equipment',
        locations.cambridge
      ),
      createItem(
        'moving-blankets-1',
        'Moving Blankets Set',
        'Set of 12 professional moving blankets for furniture protection.',
        12.00,
        owners.davidClark,
        1,
        7,
        'moving-equipment',
        locations.newmarket
      ),
      createItem(
        'furniture-lifter-1',
        'Furniture Lifter Kit',
        'Complete kit for moving heavy furniture with sliders and lifting tools.',
        10.00,
        owners.johnSmith,
        1,
        5,
        'moving-equipment',
        locations.ely
      )
    ]
  },
  {
    id: 'audio-visual',
    name: 'Audio & Visual',
    description: 'Professional audio and visual equipment',
    items: [
      createItem(
        'pa-system-1',
        'PA System',
        'Complete PA system with speakers, mixer, and microphones. Perfect for events.',
        35.00,
        owners.amyWhite,
        1,
        5,
        'audio-visual',
        locations.cambridge
      ),
      createItem(
        'drone-1',
        'Camera Drone',
        '4K camera drone with stabilization and extra batteries. Great for aerial photography.',
        40.00,
        owners.davidClark,
        1,
        3,
        'audio-visual',
        locations.newmarket
      ),
      createItem(
        'lighting-kit-1',
        'Professional Lighting Kit',
        'Studio lighting kit with softboxes and stands. Ideal for photography/video.',
        25.00,
        owners.lisaBrown,
        1,
        7,
        'audio-visual',
        locations.haverhill
      )
    ]
  },
  {
    id: 'musical-instruments',
    name: 'Musical Instruments',
    description: 'Quality musical instruments for practice or performance',
    items: [
      createItem(
        'keyboard-1',
        'Digital Piano',
        '88-key weighted digital piano with stand and pedals. Perfect for practice.',
        20.00,
        owners.emmaDavis,
        2,
        14,
        'musical-instruments',
        locations.cambridge
      ),
      createItem(
        'guitar-1',
        'Acoustic Guitar',
        'Professional acoustic guitar with hard case. Includes tuner and picks.',
        15.00,
        owners.mikeWilson,
        2,
        10,
        'musical-instruments',
        locations.ely
      ),
      createItem(
        'drums-1',
        'Electronic Drum Kit',
        'Complete electronic drum kit with headphones. Apartment-friendly.',
        30.00,
        owners.davidClark,
        2,
        7,
        'musical-instruments',
        locations.newmarket
      )
    ]
  }
]; 